import {
    dehydrate,
    hydrate,
    type DehydratedState,
    QueryClient,
    VueQueryPlugin,
    type VueQueryPluginOptions,
} from '@tanstack/vue-query'
import {intersection, isEmpty} from 'lodash'
import {QUERY_STORAGE_KEYS} from "~/constants/queries";
import {clearStoredQueries, STORAGE_KEY_PREFIX} from "~/helpers/queryUtils";
import {useUserStore} from "~/stores/user";

export const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 1000 } },
})

export default defineNuxtPlugin((nuxt) => {
    const options: VueQueryPluginOptions = { queryClient }
    nuxt.vueApp.use(VueQueryPlugin, options)
    const userStore = useUserStore()

    const matchKey = (queryKey: string[], targetKey: string[]) => {
        return intersection(queryKey, targetKey).length === targetKey.length
    }

    const getStorage = (type: 'localStorage' | 'sessionStorage') =>
        type === 'localStorage' ? localStorage : sessionStorage

    nuxt.hooks.hook('app:created', async () => {
        for (const { key, storage, persistOnLogout } of QUERY_STORAGE_KEYS) {
            if (persistOnLogout || userStore.isLoggedIn) {
                try {
                    const raw = getStorage(storage).getItem(STORAGE_KEY_PREFIX + JSON.stringify(key))
                    if (raw) {
                        const parsed = JSON.parse(raw) as DehydratedState
                        if (parsed?.queries?.some((q) => !isEmpty(q.state?.data))) {
                            hydrate(queryClient, parsed)
                        }
                    }
                } catch (error) {
                    console.error(`Failed to hydrate from ${storage} for key ${key}`, error)
                }
            } else {
                queryClient.removeQueries({queryKey: key})
                clearStoredQueries()
            }
        }
    })

    const dehydrateQueries = () => {
        for (const { key, storage, persistOnLogout } of QUERY_STORAGE_KEYS) {
            const dehydrated = dehydrate(queryClient, {
                shouldDehydrateQuery: (q) => Array.isArray(q.queryKey) && !isEmpty(q.state?.data) && matchKey(q.queryKey, key) && (persistOnLogout || userStore.isLoggedIn),
            })

            if (dehydrated?.queries?.some((dehydratedQuery) => !isEmpty(dehydratedQuery?.state?.data))) {
                try {
                    getStorage(storage).setItem(
                        STORAGE_KEY_PREFIX + JSON.stringify(key),
                        JSON.stringify(dehydrated)
                    )
                } catch (error) {
                    console.error(`Failed to persist query cache`, error)
                }
            }
        }
    }

    // Persist matching queries on state change
    queryClient.getQueryCache().subscribe(() => {
       dehydrateQueries()
    })

    // Final save before unload
    window.addEventListener('beforeunload', () => {
        dehydrateQueries()
    })
})